import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, X, Trophy, Trash2, GitCompare } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCompare } from "../context/CompareContext";

const ROWS = [
  { label: "University", get: (c) => c.university },
  { label: "Country", get: (c) => c.country },
  { label: "Level", get: (c) => c.level },
  { label: "QS Ranking", get: (c) => `#${c.qs}`, highlightLow: true, num: (c) => c.qs },
  { label: "Annual Fees", get: (c) => `\u20b9${c.fees}L`, highlightLow: true, num: (c) => c.fees },
  { label: "Duration", get: (c) => c.duration },
  { label: "Job Rate", get: (c) => `${c.jobRate}%`, highlightHigh: true, num: (c) => c.jobRate },
  { label: "ROI", get: (c) => c.roi },
  { label: "Scholarship", get: (c) => c.scholarship },
];

export default function Compare() {
  const navigate = useNavigate();
  const { items, remove, clear } = useCompare();

  const best = (row) => {
    if (!row.num || (!row.highlightLow && !row.highlightHigh)) return null;
    const vals = items.map(row.num);
    return row.highlightLow ? Math.min(...vals) : Math.max(...vals);
  };

  return (
    <div className="min-h-screen bg-[#f6f8fc] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-[1100px] mx-auto px-5 py-10 md:py-14">
          <div className="flex items-center justify-between mb-8">
            <div>
              <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-[#5a6274] hover:text-[#16244F] font-head text-sm mb-2"><ArrowLeft size={16} /> Back</button>
              <h1 className="font-head text-3xl font-bold text-[#16244F] flex items-center gap-2"><GitCompare size={26} className="text-[#2b8cf0]" /> Compare Programs</h1>
            </div>
            {items.length > 0 && (
              <button onClick={clear} className="inline-flex items-center gap-1.5 text-[#5a6274] hover:text-red-500 font-head font-semibold text-sm"><Trash2 size={16} /> Clear all</button>
            )}
          </div>

          {items.length === 0 ? (
            <div className="bg-white rounded-2xl cc-shadow-card p-12 text-center">
              <GitCompare size={40} className="text-[#cdd4e0] mx-auto mb-3" />
              <p className="text-[#5a6274]">No programs selected yet. Add programs from the Course Finder or University pages.</p>
              <button onClick={() => navigate("/course-finder")} className="mt-5 inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#e0730f] text-white font-head font-semibold px-6 py-3 rounded-lg transition-colors">Go to Course Finder</button>
            </div>
          ) : (
            <div className="bg-white rounded-2xl cc-shadow-card overflow-x-auto">
              <table className="w-full border-collapse min-w-[640px]">
                <thead>
                  <tr>
                    <th className="text-left p-4 w-40 align-bottom"></th>
                    {items.map((c) => (
                      <th key={c.id} className="p-4 align-top text-left">
                        <div className="relative">
                          <button onClick={() => remove(c.id)} className="absolute -top-1 right-0 text-gray-300 hover:text-red-500"><X size={16} /></button>
                          <img src={c.image} alt={c.university} className="w-full h-24 object-cover rounded-lg mb-3" />
                          <div className="font-head font-semibold text-[#16244F] leading-snug">{c.course}</div>
                          {c.fit != null && <div className="text-xs text-[#F58220] font-semibold mt-1">{c.fit}% fit</div>}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map((row) => {
                    const b = best(row);
                    return (
                      <tr key={row.label} className="border-t border-gray-100">
                        <td className="p-4 font-head font-semibold text-[#8a90a0] text-sm">{row.label}</td>
                        {items.map((c) => {
                          const isBest = b != null && row.num && row.num(c) === b;
                          return (
                            <td key={c.id} className="p-4 text-sm">
                              <span className={isBest ? "inline-flex items-center gap-1 font-bold text-[#16a34a]" : "text-[#45506b]"}>
                                {isBest && <Trophy size={13} />} {row.get(c)}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                  <tr className="border-t border-gray-100">
                    <td className="p-4"></td>
                    {items.map((c) => (
                      <td key={c.id} className="p-4">
                        <button onClick={() => navigate("/", { state: { scrollTo: "contact", requestedCounsellor: `${c.course} at ${c.university}` } })} className="inline-flex items-center justify-center w-full bg-[#F58220] hover:bg-[#e0730f] text-white font-head font-semibold text-sm py-2.5 rounded-lg transition-colors">Enquire</button>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
